import dayjs from 'dayjs';
import { Trans, useTranslation } from 'react-i18next';

import { useRefreshKboInformation, useUser } from '~/api/user';
import { Button, ExternalLink, Infotip, Tooltip } from '~/components';
import { Field, Input } from '~/components/Form';
import { useIntl } from '~/hooks/useIntl';
import { selectBestRelativeTimeFormat } from '~/utils/dates';
import { formatVatNumber } from '~/utils/string';
import { toast } from '~/utils/toast';

import { columnsStyle, lastUpdatedStyle } from './CompanyInfo.css';

export const CompanyInfo = () => {
  const { data: user } = useUser();

  const { t } = useTranslation(['settings']);

  const fields = [
    { label: t('settings:fields.vatID.label'), value: formatVatNumber(user.vatNumber) },
    { label: t('settings:fields.companyName.label'), value: user.companyName },
    { label: t('settings:fields.street.label'), value: user.address.street },
    { label: t('settings:fields.number.label'), value: user.address.number },
    { label: t('settings:fields.zipcode.label'), value: user.address.zipCode },
    { label: t('settings:fields.city.label'), value: user.address.city },
  ];

  return (
    <div className={columnsStyle}>
      {fields.map(({ label, value }) => (
        <Field.Root key={label}>
          <Field.Label>{label}</Field.Label>
          <Input disabled readOnly value={value} />
        </Field.Root>
      ))}
    </div>
  );
};

export const CompanyInfoRefreshAction = () => {
  const { data: user } = useUser();
  const mutation = useRefreshKboInformation();

  const { t } = useTranslation(['common', 'settings']);

  const daysUntilRefresh = 7 - dayjs().diff(user.kboInformationLastUpdatedAt, 'days');
  const isTooSoon = daysUntilRefresh > 0;

  return (
    <Tooltip content={t('settings:profile.info.refreshKboInformation.unavailable', { count: daysUntilRefresh })} disabled={!isTooSoon}>
      <Button
        disabled={isTooSoon}
        emphasis="secondary"
        focusableWhenDisabled
        icon="Refresh"
        loading={mutation.isPending}
        onClick={() => mutation.mutate(undefined, { onError: () => toast.danger({ description: t('common:error') }) })}
      >
        {t('settings:profile.info.refreshKboInformation.action')}
      </Button>
    </Tooltip>
  );
};

export const CompanyInfoLastUpdated = () => {
  const { data: user } = useUser();

  const { formatRelativeTime } = useIntl();
  const { t } = useTranslation(['settings']);

  return (
    <span className={lastUpdatedStyle}>
      <time dateTime={user.kboInformationLastUpdatedAt}>
        {t('settings:profile.info.refreshKboInformation.lastUpdated', {
          timeAgo: formatRelativeTime(...selectBestRelativeTimeFormat(user.kboInformationLastUpdatedAt)),
        })}
      </time>

      <Infotip>
        <Trans
          components={{
            kboLink: <ExternalLink href={`https://kbopub.economie.fgov.be/kbopub/zoeknummerform.html?nummer=${user.vatNumber.slice(2)}`} />,
          }}
          i18nKey="settings:profile.info.refreshKboInformation.tooltip"
        />
      </Infotip>
    </span>
  );
};
